import { Box, Stack, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { useMemo } from 'react'
import { BatteryMeter } from 'src/components/VehicleInfo'
import VehicleTopMark from 'src/components/vehicle/VehicleTopMark'
import { bearing } from 'src/format'
import { VehiclesQuery } from 'src/generated'
import { layout } from 'src/theme/tokens.generated'
import { markerState, markerStateOf } from './markerState'

// ----------------------------------------------------------------------
// One vehicle on the map.
//
// A marker is a pill an operator reads at a glance while scanning a city: a
// tinted dot saying what state the car is in, its name, and its charge, with
// the car itself standing on the street underneath.
//
// **The dot is the legend's swatch, at the legend's size.** The states, the
// labels and the hues are one table shared with the legend (see `markerState`)
// and now the mark is shared too, through `layout.mapMarkerDot`. It was a
// 24px disc with a glyph in it, and the glyph was the problem rather than the
// disc: at marker scale a `circle-dot` reads as a target reticle printed on
// the street, four differently-glyphed discs are not told apart at city zoom
// anyway, and a key drawn as a small plain dot beside a map of large marked
// ones is a key that has to be translated before it can be used. One mark, one
// size, one table. The divergence this opens, that the state is carried by
// colour and by the accessible name rather than by a second visual channel, is
// recorded in
// `docs/DESIGN_SYSTEM.md`.
//
// The badge used to paint the categorical chip background, which is the quiet
// pair meant for a chip on a console surface: four of them side by side on a
// basemap measured 1.00:1 to 1.14:1 from each other, so the colour said
// nothing at all and the pill had to be read. It is the vivid hue now.
//
// **The charge is the fleet list's own meter.** It drew a bare percentage
// first, which made the map the one surface in the product where a battery had
// no tier — on the screen an operator uses to decide which car to send someone
// to. That was fixed with a mark of its own: a tiered glyph and a figure,
// compact enough for a pill. Two forms of one reading, and the second form was
// the defect: an operator moving between Fleet and the map read the same car's
// charge as a cell with a fill length on one screen and as a battery glyph
// with bars to count on the other, and had to learn that those were the same
// fact. There is one battery in the product now, and it is this one;
// `batteryAgreement.test.tsx` holds the surfaces to it.
//
// **The car under the pill is that car's colour.** It is how an operator
// standing in a car park matches a pin to a vehicle in front of them, and it
// is the thing the old bitmap marker did well: four PNGs carried the *state*
// and were rightly deleted for it, but the silhouette and the paint were a
// separate reading and went out with them. `VehicleTopMark` draws geometry
// tinted from the vehicle's own `exterior`, so the state vocabulary stays in
// the palette where the legend shares it and the car says only what colour it
// is. A vehicle with no exterior recorded draws no car: a grey default would
// be this console asserting a colour it has not been told.
//
// **The badge is the state, and nothing else is drawn around it.** A stale
// position used to add a dashed ring: a car that has not reported for longer
// than the telemetry policy allows may not be where the map says it is, and
// the ring said so without relying on the muted tint. It was removed because
// of how often it fires. The policy is minutes, a parked fleet reports in
// hours, and so the exception was the rule — a map of forty cars drew a dashed
// outline round almost every badge whatever its state, Available and Disabled
// alike, and a mark that is on nearly always is chrome rather than a signal.
//
// The reading it carried is not lost, and moving it is the whole reason this
// was safe to take off. It is in `MapVehicleSheet`, on the panel an operator
// opens before acting on a car, as `FreshnessLabel` — which states the age in
// words and in the freshness ink instead of asking anyone to know what a
// dashed circle means.
//
// **And the car points where the car points.** Tesla reports the heading in
// degrees clockwise from true north with the position, so the mark turns by
// exactly that. This was left out when the marker was rebuilt, on the argument
// that a parked fleet's heading is rarely what anyone acts on; it is, in the
// one place a fleet map is actually used, which is a person standing in a car
// park deciding which of two identical white cars is theirs. The compass point
// is in the marker's `title`, because a rotation is a drawing and not
// something a screen reader can read.
// ----------------------------------------------------------------------

type VehicleMarkerProps = {
  mapVehicle: Pick<
    VehiclesQuery['vehicles']['vehicles'][0],
    'id' | 'name' | 'latestStatus' | 'rental' | 'exterior'
  >
  onSelect: (id: number) => void
  selected?: boolean
}

const VehicleMarker = ({
  mapVehicle,
  onSelect,
  selected = false,
}: VehicleMarkerProps) => {
  const state = useMemo(() => markerStateOf(mapVehicle), [mapVehicle])

  const { tint, label } = markerState(state)

  const batteryLevel = mapVehicle.latestStatus?.batteryLevel ?? null
  // Which way the car is pointing, from the same reading the pin's position
  // came from. `null` when there is no reading at all: the field is non-null
  // on the wire, so an absent status would otherwise arrive as due north.
  const heading = mapVehicle.latestStatus
    ? mapVehicle.latestStatus.heading
    : null
  const facing = bearing(heading)

  return (
    // **The whole marker is the target, and the button is only where the
    // keyboard lands.** The label and the car are one pin: pointing at the car
    // and getting nothing is the reported defect, and it is the half a person
    // aims at, because it is the half that looks like the vehicle. So the
    // handler sits on the stack, which is what a click on either half reaches,
    // and the pill keeps being the one `<button>` so there is still exactly one
    // thing in the accessibility tree per vehicle. A button's own activation
    // dispatches a click that bubbles here, so Enter and Space arrive too.
    //
    // The pill stays on top: it is the thing being scanned, and a label under a
    // car is a label that collides with the next car down.
    <Stack
      alignItems="center"
      sx={{ gap: 0.25, cursor: 'pointer' }}
      onClick={() => onSelect(mapVehicle.id)}
    >
      {/* The drop shadow is the marker's, not the button's. It is what lifts a
          pin off a photographic basemap so it reads as a thing standing on the
          map rather than a label printed into it: a marker treatment, the same
          way the halo is, and the system records it as one. It is not an
          elevation the pressable rests on: nothing in the console is pressed by
          pushing a shadow down. So it sits on the frame, the pressable sits
          flush inside it at the same radius, and the painted pixels are the
          ones that were always there. */}
      <Box
        sx={{
          display: 'inline-flex',
          maxWidth: '100%',
          borderRadius: (theme) => theme.radii.pill,
          boxShadow: (theme) => theme.customShadows.dropdown,
          // **The label is on top of the car, and it has to say so.** The car
          // turns to its heading, and a rotated element keeps a rectangular
          // hit area: at 47 degrees a 44px square covers 62px, which reaches
          // up over the pill sitting two pixels above it. The car is later in
          // the document, so without this it takes the pointer off the label,
          // and the pill stops answering hover on the half a person is most
          // likely to aim at.
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack
          component="button"
          type="button"
          direction="row"
          alignItems="center"
          aria-label={`${mapVehicle.name}, ${state}`}
          // The state in words, on the pointer. The dot is a colour and the
          // legend is the key to it; this is the same answer without crossing
          // the screen to read the key.
          //
          // And the compass point, because the car turning is a drawing and
          // not a sentence: somebody who cannot see it turn gets nothing from
          // it otherwise, and nobody reads `facing 47 degrees`.
          title={
            facing
              ? `${mapVehicle.name}: ${label}, facing ${facing}`
              : `${mapVehicle.name}: ${label}`
          }
          aria-pressed={selected}
          // A marker is a pin, and a pin is a pill: it is the one shape on this
          // screen that has to survive being read at a glance against aerial
          // photography, which is why the shape and its halo are a marker
          // treatment in the system rather than the console's button shape.
          // Declared on the element so the rendered-page audit reads the
          // exception off the control, instead of this being a third name on a
          // list kept somewhere else: the list is the thing that goes stale.
          data-shape="marker"
          sx={{
            'gap': 0.75,
            'px': 1,
            'py': 0.5,
            'cursor': 'pointer',
            // Wide enough for the name, which is what an operator scans for.
            // The meter is a cell, a gap and a figure where the mark it replaced
            // was a 14px glyph and a hair, so the ceiling gives back what the
            // reading took rather than taking it out of the name.
            'maxWidth': 248,
            'borderRadius': (theme) => theme.radii.pill,
            'backgroundColor': 'background.paper',
            'border': `${layout.mapMarkerRing}px solid`,
            'borderColor': selected ? 'primary.main' : 'divider',
            'transition': (theme) => theme.hover.transition,
            // A marker is a surface, so it answers like one. It was a pressable
            // pill sitting on a map with nothing to say that it could be pressed.
            '&:hover': {
              backgroundColor: (theme) => theme.hover.surface,
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: (theme) =>
                `0 0 0 ${layout.focusRingWidth}px ${theme.palette.accentRing}`,
            },
          }}
        >
          <Box
            data-testid="marker-badge"
            sx={{
              flexShrink: 0,
              width: layout.mapMarkerDot,
              height: layout.mapMarkerDot,
              borderRadius: '50%',
              // The mark's own colour and nothing on it. Not the chip pair:
              // that is a fill for a console surface, and this disc sits on a
              // basemap nothing here controls.
              backgroundColor: (theme: Theme) => theme.palette.vivid[tint],
            }}
          />

          {/* **`lineHeight: 1`, so the box is the word and not the leading.**
              Centring a 10px dot against a line box is centring it against
              half a line of air: body2's leading puts about five pixels of
              nothing under the descender, the flex line takes its height from
              the tallest item in the pill, and the dot ends up sitting low
              against the letters it is supposed to sit beside. With the
              leading gone every item in the pill is its own ink, and they
              centre on each other. */}
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              lineHeight: 1,
              color: 'text.primary',
              minWidth: 0,
            }}
          >
            {mapVehicle.name}
          </Typography>

          {/* The same component the fleet list draws, at the same size, so the
              two screens answer identically about the same car. The gap is the
              meter's own; the pill only says it may not be squeezed. */}
          <BatteryMeter level={batteryLevel} sx={{ flexShrink: 0 }} />
        </Stack>
      </Box>

      {/* Under the label, standing on the street. `VehicleTopMark` returns
          nothing at all for a vehicle with no recorded colour, so the marker
          is the pill on its own rather than the pill over a placeholder.

          It takes pointer events rather than refusing them: this is the half
          of the pin a person aims at. No drop shadow is added over it, because
          the render carries its own. */}
      <VehicleTopMark exterior={mapVehicle.exterior} heading={heading} />
    </Stack>
  )
}

export { VehicleMarker }
